import { useEffect, useState } from "react";
import { RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Mail wording for one customer. Subject and body are the admin's; the app fills the braces. */
export default function MailDrafts({ customer, onSaved }) {
  const [mails, setMails] = useState([]);
  const [standard, setStandard] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/mail-templates", { headers: { "X-Customer-Id": customer.id } })
      .then(({ data }) => {
        setMails(data.mails);
        setStandard(data.standard);
        setPlaceholders(data.placeholders);
      })
      .catch((e) => toast.error(errText(e)));
  }, [customer.id]);

  const set = (key, patch) =>
    setMails((list) => list.map((m) => (m.key === key ? { ...m, ...patch } : m)));

  const reset = (key) => {
    const base = standard.find((s) => s.key === key);
    if (base) set(key, { subject: base.subject, body: base.body });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, { mails });
      toast.success("Mail wording saved");
      await onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel p-3 sm:p-4" data-testid="mail-drafts-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">Mail drafts for {customer.name}</h2>
        <Button size="sm" onClick={save} disabled={busy} data-testid="mail-drafts-save-button">
          <Save className="mr-2 h-4 w-4" />
          Save the wording
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Anything in braces is filled in by the app when the mail is drafted. {"{header}"} is the four-line PO block
        and {"{ldFacts}"} is the numbered LD exemption record, so you never retype either.
      </p>

      <details className="mt-2 text-xs" data-testid="mail-placeholders">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          What you can drop in ({placeholders.length})
        </summary>
        <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {placeholders.map((p) => (
            <div key={p.key} className="flex gap-2">
              <span className="font-mono text-primary">{`{${p.key}}`}</span>
              <span className="text-muted-foreground">{p.what}</span>
            </div>
          ))}
        </div>
      </details>

      <div className="mt-3 space-y-3">
        {mails.map((m) => (
          <div key={m.key} className="rounded-md border border-border p-3" data-testid={`mail-row-${m.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-medium">{m.label}</span>
              <button
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
                onClick={() => reset(m.key)}
                data-testid={`mail-reset-${m.key}`}
              >
                <RotateCcw className="h-3 w-3" />
                Reset to the standard wording
              </button>
            </div>
            <div className="mt-2">
              <Label className="field-label">Subject</Label>
              <Input
                value={m.subject || ""}
                onChange={(e) => set(m.key, { subject: e.target.value })}
                className="h-9"
                data-testid={`mail-subject-${m.key}`}
              />
            </div>
            <div className="mt-2">
              <Label className="field-label">Body</Label>
              <Textarea
                rows={8}
                value={m.body || ""}
                onChange={(e) => set(m.key, { body: e.target.value })}
                className="font-mono text-xs"
                data-testid={`mail-body-${m.key}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
